export type Lang = 'fr' | 'ar' | 'en' | 'es' | 'it';

export const langLabels: Record<Lang, string> = {
  fr: 'FR',
  ar: 'AR',
  en: 'EN',
  es: 'ES',
  it: 'IT',
};

export const langNames: Record<Lang, string> = {
  fr: 'Français',
  ar: 'العربية',
  en: 'English',
  es: 'Español',
  it: 'Italiano',
};

export const isRTL = (lang: Lang) => lang === 'ar';

type Translations = Record<Lang, Record<string, string>>;

const t: Translations = {
  fr: {
    // Nav
    nav_home: 'Accueil',
    nav_about: 'À Propos',
    nav_rooms: 'Hébergement',
    nav_dining: 'Restauration',
    nav_activities: 'Activités',
    nav_gallery: 'Galerie',
    nav_contact: 'Contact',

    // Hero
    hero_location: 'Moyen-Atlas · Azrou · Maroc',
    hero_title_1: "Ferme d'hôtes",
    hero_title_2: 'La Vallée',
    hero_desc: "Votre havre de paix au cœur du Moyen-Atlas. Une expérience authentique et chaleureuse, loin de l'agitation de la ville.",
    hero_cta: 'Découvrir nos chambres',
    hero_slide: 'Diapositive',

    // About
    about_label: 'Notre Histoire',
    about_title_1: "L'hospitalité",
    about_title_2: 'au cœur de tout',
    about_p1: "Au Maroc, l'hospitalité – <i>tinnubga</i> en amazighe, <i>diafa</i> en arabe – est bien plus qu'une coutume : c'est une valeur sacrée, un devoir social et une joie partagée. Accueillir un invité, c'est lui ouvrir sa maison et son cœur.",
    about_p2: "À la <b>Ferme d'hôtes La Vallée</b>, nous perpétuons cette valeur ancestrale dans l'expérience touristique moderne. Chaque visiteur est reçu comme un membre de la famille : accueil chaleureux, cuisine locale authentique, découverte culturelle et naturelle du Moyen Atlas.",
    about_p3: "Notre ferme n'est pas seulement un lieu de séjour, c'est un espace où l'hospitalité marocaine prend vie. Le calme de la campagne, la beauté des paysages et la chaleur humaine qui font du Maroc une terre d'accueil unique.",

    // Rooms
    rooms_label: 'Hébergement',
    rooms_title: 'Nos Chambres',
    rooms_desc: "Toutes nos chambres sont équipées d'une armoire et d'une salle de bains privative avec des articles de toilette gratuits.",
    rooms_book: 'Réserver',
    room_double: 'Chambre Double',
    room_double_desc: 'Idéale pour les couples, avec un lit double confortable et une décoration rustique soignée.',
    room_quadruple: 'Chambre Quadruple',
    room_quadruple_desc: "Parfaite pour les familles ou groupes d'amis, spacieuse et conviviale.",
    room_triple: 'Chambre Triple',
    room_triple_desc: 'Idéale en famille ou entre amis, un espace généreux garantissant confort et praticité.',
    room_f_double_bed: 'Lit double',
    room_f_private_bath: 'Salle de bains privative',
    room_f_garden_view: 'Vue sur le jardin',
    room_f_quad_beds: '2 lits simples + 1 grand lit double',
    room_f_mountain_view: 'Vue sur la montagne',
    room_f_triple_beds: '3 lits simples',
    room_f_field_view: 'Vue sur les champs',

    // Dining
    dining_gastro: 'Gastronomie',
    dining_title_1: 'Saveurs',
    dining_title_2: 'du Terroir',
    dining_text: "Chez nous, chaque plat raconte une histoire. Nos recettes marocaines sont préparées à la commande, avec des ingrédients frais du terroir sélectionnés avec soin. Nous privilégions le fait maison et l'authenticité : rien n'est précuit ni préparé à l'avance. Pour vous garantir un repas d'exception, nous vous recommandons de réserver vos plats à l'avance, afin que notre chef puisse sublimer les meilleurs produits du Moyen Atlas pour vous.",
    dining_menu: 'Voir le Menu',
    dining_local: 'Produits',
    dining_local2: 'locaux',

    // Menu book
    menu_tradition: 'Tradition et terroir du Moyen Atlas',
    menu_title: 'Menu',
    menu_carte: 'à la carte',
    menu_year: '— 2026 —',
    menu_farmname: "Ferme d'hôtes",
    menu_farmname2: 'La Vallée',
    menu_open: 'Ouvrir le menu',
    menu_header: 'Menu à la Carte',
    menu_header_sub: "Ferme d'hôtes La Vallée",
    menu_cover: 'Couverture',
    menu_prev: 'Précédent',
    menu_next: 'Suivant',
    menu_close: 'Fermer',
    menu_bonappetit: 'Bon Appétit',
    menu_sec_breakfast: 'Petit Déjeuner',
    menu_sec_starters: 'Entrées',
    menu_sec_moroccan: 'Plats Marocains',
    menu_sec_international: 'Cuisine Internationale',
    menu_sec_kids: 'Plats pour Enfants',
    menu_sec_sides: 'Accompagnements',
    menu_sec_desserts: 'Desserts',
    menu_sec_drinks: 'Boissons',

    // Activities
    act_label: 'Loisirs',
    act_title: 'Que Faire à la Ferme ?',
    act_pool: 'Détente Piscine',
    act_pool_desc: 'Profitez de notre piscine extérieure pour vous rafraîchir tout en admirant le paysage.',
    act_garden: 'Promenades Jardin',
    act_garden_desc: 'Flânez dans notre jardin luxuriant et découvrez notre potager bio.',
    act_animals: 'Animaux de la Ferme',
    act_animals_desc: 'Une expérience unique pour petits et grands au contact de nos animaux.',
    act_games: 'Jeux & Loisirs',
    act_games_desc: 'Parties de billard, ping-pong ou fléchettes pour des moments conviviaux.',
    act_hiking: 'Randonnées Guidées',
    act_hiking_desc: 'Explorez la magnifique forêt de cèdres avec nos guides locaux experts.',
    act_lounge: 'Détente au Salon',
    act_lounge_desc: 'Profitez de notre salon cheminée pour lire ou déguster un thé à la menthe.',

    // Testimonials
    testi_label: 'Témoignages',
    testi_title: 'Ce que nos visiteurs disent',

    // Gallery
    gallery_label: 'Photos',
    gallery_title: 'Notre Galerie',
    gallery_desc: "Découvrez en images la beauté de notre ferme d'hôtes, ses paysages et son atmosphère unique.",
    gallery_all: 'Tout',
    gallery_landscape: 'Paysage',
    gallery_garden: 'Jardin',
    gallery_rooms: 'Chambres',
    gallery_interior: 'Intérieur',
    gallery_farm: 'La Ferme',
    gallery_animals: 'Animaux',
    gallery_dining: 'Restauration',
    gallery_activities: 'Activités',

    // Map
    map_label: 'Localisation',
    map_title: 'Nous Trouver',
    map_desc: "Située au cœur du Moyen-Atlas, notre ferme d'hôtes vous accueille à Azrou, dans un cadre naturel exceptionnel.",
    map_name: "Ferme d'hôtes La Vallée",
    map_address: 'Takmout aoud elma, route Meknès — Azrou, Maroc',
    map_open: 'Ouvrir dans Google Maps',

    // Contact
    contact_label: 'Contact',
    contact_title: 'Contactez-nous',
    contact_desc: 'Réservez votre séjour dès maintenant ou posez-nous vos questions. Nous sommes impatients de vous accueillir.',
    contact_address_title: 'Notre adresse',
    contact_address: 'Takmout aoud elma, route Meknès AZROU\ndouar boudraa, 53123 Azrou, Maroc',
    contact_phone: 'Téléphone',
    contact_reservation: 'Réservation',
    contact_email: 'Email',

    // Footer
    footer_tagline: 'Votre havre de paix au cœur du Moyen-Atlas.',
    footer_rights: "© 2024 Ferme d'hôtes La Vallée. Tous droits réservés.",
    footer_powered: 'Powered by',

    // WhatsApp
    wa_label: 'Réservation & Disponibilité',
    wa_aria: 'Réserver via WhatsApp',
    wa_room_msg: 'Bonjour, je souhaite réserver la',

    // Welcome toast
    welcome_title: 'Bienvenue ! 🌿',
    welcome_text: 'Découvrez notre havre de paix au cœur du Moyen-Atlas marocain.',
    welcome_close: 'Fermer',

    // Music
    music_on: 'Vivaldi — Les Quatre Saisons',
    music_off: '♪ Printemps — Allegro',
    music_aria_on: 'Activer la musique',
    music_aria_off: 'Couper la musique',

    // Scroll
    scroll_top: 'Retour en haut',
  },

  ar: {
    nav_home: 'الرئيسية',
    nav_about: 'من نحن',
    nav_rooms: 'الإقامة',
    nav_dining: 'المطعم',
    nav_activities: 'الأنشطة',
    nav_gallery: 'المعرض',
    nav_contact: 'اتصل بنا',

    hero_location: 'الأطلس المتوسط · أزرو · المغرب',
    hero_title_1: "Ferme d'hôtes",
    hero_title_2: 'La Vallée',
    hero_desc: 'واحتك من السلام في قلب الأطلس المتوسط. تجربة أصيلة ودافئة بعيدًا عن صخب المدينة.',
    hero_cta: 'اكتشف غرفنا',
    hero_slide: 'شريحة',

    about_label: 'قصتنا',
    about_title_1: 'الضيافة',
    about_title_2: 'في صميم كل شيء',
    about_p1: 'في المغرب، الضيافة – <i>تينوبگا</i> بالأمازيغية، <i>الضيافة</i> بالعربية – أكثر من مجرد عادة: إنها قيمة مقدسة وواجب اجتماعي وفرحة مشتركة. استقبال الضيف يعني فتح البيت والقلب له.',
    about_p2: 'في <b>مزرعة ضيافة الوادي</b>، نحافظ على هذه القيمة العريقة في تجربة سياحية عصرية. كل زائر يُستقبل كفرد من العائلة: ترحيب حار، مطبخ محلي أصيل، واكتشاف ثقافي وطبيعي للأطلس المتوسط.',
    about_p3: 'مزرعتنا ليست مجرد مكان للإقامة، بل هي فضاء تنبض فيه الضيافة المغربية بالحياة. هدوء الريف وجمال المناظر ودفء الإنسان يجعلون المغرب أرض استقبال فريدة.',

    rooms_label: 'الإقامة',
    rooms_title: 'غرفنا',
    rooms_desc: 'جميع غرفنا مجهزة بخزانة وحمام خاص مع مستلزمات العناية الشخصية المجانية.',
    rooms_book: 'احجز',
    room_double: 'غرفة مزدوجة',
    room_double_desc: 'مثالية للأزواج، مع سرير مزدوج مريح وديكور ريفي أنيق.',
    room_quadruple: 'غرفة رباعية',
    room_quadruple_desc: 'مثالية للعائلات أو مجموعات الأصدقاء، واسعة ومريحة.',
    room_triple: 'غرفة ثلاثية',
    room_triple_desc: 'مثالية للعائلة أو الأصدقاء، مساحة سخية تضمن الراحة والعملية.',
    room_f_double_bed: 'سرير مزدوج',
    room_f_private_bath: 'حمام خاص',
    room_f_garden_view: 'إطلالة على الحديقة',
    room_f_quad_beds: '2 سرير فردي + 1 سرير مزدوج كبير',
    room_f_mountain_view: 'إطلالة على الجبل',
    room_f_triple_beds: '3 أسرة فردية',
    room_f_field_view: 'إطلالة على الحقول',

    dining_gastro: 'فن الطهي',
    dining_title_1: 'نكهات',
    dining_title_2: 'الأرض',
    dining_text: 'عندنا، كل طبق يروي قصة. وصفاتنا المغربية تُحضّر حسب الطلب، بمكونات طازجة من الأرض مختارة بعناية. نفضّل المنزلي والأصيل: لا شيء مطبوخ مسبقًا. لضمان وجبة استثنائية، ننصحكم بالحجز مسبقًا ليتمكن طاهينا من إبداع أفضل منتجات الأطلس المتوسط لكم.',
    dining_menu: 'عرض القائمة',
    dining_local: 'منتجات',
    dining_local2: 'محلية',

    menu_tradition: 'تقاليد وتراث الأطلس المتوسط',
    menu_title: 'القائمة',
    menu_carte: 'حسب الطلب',
    menu_year: '— 2026 —',
    menu_farmname: 'مزرعة ضيافة',
    menu_farmname2: 'الوادي',
    menu_open: 'افتح القائمة',
    menu_header: 'القائمة حسب الطلب',
    menu_header_sub: 'مزرعة ضيافة الوادي',
    menu_cover: 'الغلاف',
    menu_prev: 'السابق',
    menu_next: 'التالي',
    menu_close: 'إغلاق',
    menu_bonappetit: 'بالصحة والعافية',
    menu_sec_breakfast: 'فطور الصباح',
    menu_sec_starters: 'المقبلات',
    menu_sec_moroccan: 'أطباق مغربية',
    menu_sec_international: 'مطبخ عالمي',
    menu_sec_kids: 'أطباق للأطفال',
    menu_sec_sides: 'مرافقات',
    menu_sec_desserts: 'حلويات',
    menu_sec_drinks: 'مشروبات',

    act_label: 'ترفيه',
    act_title: 'ماذا تفعل في المزرعة؟',
    act_pool: 'استرخاء بالمسبح',
    act_pool_desc: 'استمتع بمسبحنا الخارجي للانتعاش مع الاستمتاع بالمنظر الطبيعي.',
    act_garden: 'نزهات في الحديقة',
    act_garden_desc: 'تنزه في حديقتنا الخضراء واكتشف مزرعتنا العضوية.',
    act_animals: 'حيوانات المزرعة',
    act_animals_desc: 'تجربة فريدة للصغار والكبار مع حيواناتنا.',
    act_games: 'ألعاب وترفيه',
    act_games_desc: 'بلياردو وتنس طاولة ورمي السهام لأوقات ممتعة.',
    act_hiking: 'رحلات مشي موجّهة',
    act_hiking_desc: 'استكشف غابة الأرز الرائعة مع مرشدينا المحليين.',
    act_lounge: 'استرخاء في الصالون',
    act_lounge_desc: 'استمتع بصالون المدفأة للقراءة أو تذوق شاي بالنعناع.',

    testi_label: 'شهادات',
    testi_title: 'ماذا يقول زوارنا',

    gallery_label: 'صور',
    gallery_title: 'معرضنا',
    gallery_desc: 'اكتشف بالصور جمال مزرعة الضيافة ومناظرها وأجوائها الفريدة.',
    gallery_all: 'الكل',
    gallery_landscape: 'مناظر',
    gallery_garden: 'حديقة',
    gallery_rooms: 'غرف',
    gallery_interior: 'داخلي',
    gallery_farm: 'المزرعة',
    gallery_animals: 'حيوانات',
    gallery_dining: 'مطعم',
    gallery_activities: 'أنشطة',

    map_label: 'الموقع',
    map_title: 'كيف تجدنا',
    map_desc: 'تقع في قلب الأطلس المتوسط، مزرعتنا ترحب بكم في أزرو في إطار طبيعي استثنائي.',
    map_name: 'مزرعة ضيافة الوادي',
    map_address: 'تاكموت أود الما، طريق مكناس — أزرو، المغرب',
    map_open: 'فتح في خرائط جوجل',

    contact_label: 'اتصل بنا',
    contact_title: 'تواصل معنا',
    contact_desc: 'احجز إقامتك الآن أو اطرح أسئلتك. نحن في انتظاركم بفارغ الصبر.',
    contact_address_title: 'عنواننا',
    contact_address: 'تاكموت أود الما، طريق مكناس أزرو\nدوار بودراع، 53123 أزرو، المغرب',
    contact_phone: 'الهاتف',
    contact_reservation: 'الحجز',
    contact_email: 'البريد الإلكتروني',

    footer_tagline: 'واحتك من السلام في قلب الأطلس المتوسط.',
    footer_rights: '© 2024 مزرعة ضيافة الوادي. جميع الحقوق محفوظة.',
    footer_powered: 'بدعم من',

    wa_label: 'الحجز والتوفر',
    wa_aria: 'احجز عبر واتساب',
    wa_room_msg: 'مرحبًا، أود حجز',

    welcome_title: 'مرحبًا بكم! 🌿',
    welcome_text: 'اكتشفوا واحتنا من السلام في قلب الأطلس المتوسط المغربي.',
    welcome_close: 'إغلاق',

    music_on: 'فيفالدي — الفصول الأربعة',
    music_off: '♪ الربيع — أليغرو',
    music_aria_on: 'تشغيل الموسيقى',
    music_aria_off: 'إيقاف الموسيقى',

    scroll_top: 'العودة للأعلى',
  },

  en: {
    nav_home: 'Home',
    nav_about: 'About',
    nav_rooms: 'Rooms',
    nav_dining: 'Dining',
    nav_activities: 'Activities',
    nav_gallery: 'Gallery',
    nav_contact: 'Contact',

    hero_location: 'Middle Atlas · Azrou · Morocco',
    hero_title_1: "Ferme d'hôtes",
    hero_title_2: 'La Vallée',
    hero_desc: 'Your haven of peace in the heart of the Middle Atlas. An authentic and warm experience, far from the hustle of the city.',
    hero_cta: 'Discover our rooms',
    hero_slide: 'Slide',

    about_label: 'Our Story',
    about_title_1: 'Hospitality',
    about_title_2: 'at the heart of everything',
    about_p1: "In Morocco, hospitality – <i>tinnubga</i> in Amazigh, <i>diafa</i> in Arabic – is much more than a custom: it is a sacred value, a social duty, and a shared joy. Welcoming a guest means opening one's home and heart.",
    about_p2: "At <b>Guest Farm La Vallée</b>, we carry on this ancestral value in the modern tourism experience. Every visitor is received as a family member: warm welcome, authentic local cuisine, and cultural and natural discovery of the Middle Atlas.",
    about_p3: "Our farm is not just a place to stay, it is a space where Moroccan hospitality comes alive. The calm of the countryside, the beauty of the landscapes, and the human warmth that make Morocco a unique land of welcome.",

    rooms_label: 'Accommodation',
    rooms_title: 'Our Rooms',
    rooms_desc: 'All our rooms are equipped with a wardrobe and a private bathroom with free toiletries.',
    rooms_book: 'Book Now',
    room_double: 'Double Room',
    room_double_desc: 'Ideal for couples, with a comfortable double bed and refined rustic decor.',
    room_quadruple: 'Quadruple Room',
    room_quadruple_desc: 'Perfect for families or groups of friends, spacious and convivial.',
    room_triple: 'Triple Room',
    room_triple_desc: 'Ideal for families or friends, a generous space ensuring comfort and practicality.',
    room_f_double_bed: 'Double bed',
    room_f_private_bath: 'Private bathroom',
    room_f_garden_view: 'Garden view',
    room_f_quad_beds: '2 single beds + 1 large double bed',
    room_f_mountain_view: 'Mountain view',
    room_f_triple_beds: '3 single beds',
    room_f_field_view: 'Field view',

    dining_gastro: 'Gastronomy',
    dining_title_1: 'Local',
    dining_title_2: 'Flavors',
    dining_text: "Here, every dish tells a story. Our Moroccan recipes are prepared to order, with fresh local ingredients carefully selected. We favor homemade and authentic: nothing is pre-cooked or prepared in advance. To guarantee you an exceptional meal, we recommend booking your dishes in advance so our chef can bring out the best of the Middle Atlas produce for you.",
    dining_menu: 'View Menu',
    dining_local: 'Local',
    dining_local2: 'products',

    menu_tradition: 'Tradition and terroir of the Middle Atlas',
    menu_title: 'Menu',
    menu_carte: 'à la carte',
    menu_year: '— 2026 —',
    menu_farmname: 'Guest Farm',
    menu_farmname2: 'La Vallée',
    menu_open: 'Open menu',
    menu_header: 'À la Carte Menu',
    menu_header_sub: "Guest Farm La Vallée",
    menu_cover: 'Cover',
    menu_prev: 'Previous',
    menu_next: 'Next',
    menu_close: 'Close',
    menu_bonappetit: 'Bon Appétit',
    menu_sec_breakfast: 'Breakfast',
    menu_sec_starters: 'Starters',
    menu_sec_moroccan: 'Moroccan Dishes',
    menu_sec_international: 'International Cuisine',
    menu_sec_kids: "Children's Dishes",
    menu_sec_sides: 'Side Dishes',
    menu_sec_desserts: 'Desserts',
    menu_sec_drinks: 'Beverages',

    act_label: 'Leisure',
    act_title: 'What to Do at the Farm?',
    act_pool: 'Pool Relaxation',
    act_pool_desc: 'Enjoy our outdoor pool to cool off while admiring the landscape.',
    act_garden: 'Garden Walks',
    act_garden_desc: 'Stroll through our lush garden and discover our organic vegetable patch.',
    act_animals: 'Farm Animals',
    act_animals_desc: 'A unique experience for young and old in contact with our animals.',
    act_games: 'Games & Leisure',
    act_games_desc: 'Billiards, ping-pong, or darts for convivial moments.',
    act_hiking: 'Guided Hikes',
    act_hiking_desc: 'Explore the magnificent cedar forest with our expert local guides.',
    act_lounge: 'Lounge Relaxation',
    act_lounge_desc: 'Enjoy our fireplace lounge for reading or savoring mint tea.',

    testi_label: 'Testimonials',
    testi_title: 'What our visitors say',

    gallery_label: 'Photos',
    gallery_title: 'Our Gallery',
    gallery_desc: "Discover in images the beauty of our guest farm, its landscapes and unique atmosphere.",
    gallery_all: 'All',
    gallery_landscape: 'Landscape',
    gallery_garden: 'Garden',
    gallery_rooms: 'Rooms',
    gallery_interior: 'Interior',
    gallery_farm: 'The Farm',
    gallery_animals: 'Animals',
    gallery_dining: 'Dining',
    gallery_activities: 'Activities',

    map_label: 'Location',
    map_title: 'Find Us',
    map_desc: "Located in the heart of the Middle Atlas, our guest farm welcomes you in Azrou, in an exceptional natural setting.",
    map_name: "Guest Farm La Vallée",
    map_address: 'Takmout aoud elma, route Meknès — Azrou, Morocco',
    map_open: 'Open in Google Maps',

    contact_label: 'Contact',
    contact_title: 'Contact Us',
    contact_desc: 'Book your stay now or ask us your questions. We look forward to welcoming you.',
    contact_address_title: 'Our address',
    contact_address: 'Takmout aoud elma, route Meknès AZROU\ndouar boudraa, 53123 Azrou, Morocco',
    contact_phone: 'Phone',
    contact_reservation: 'Reservation',
    contact_email: 'Email',

    footer_tagline: 'Your haven of peace in the heart of the Middle Atlas.',
    footer_rights: "© 2024 Guest Farm La Vallée. All rights reserved.",
    footer_powered: 'Powered by',

    wa_label: 'Booking & Availability',
    wa_aria: 'Book via WhatsApp',
    wa_room_msg: 'Hello, I would like to book the',

    welcome_title: 'Welcome! 🌿',
    welcome_text: 'Discover our haven of peace in the heart of the Moroccan Middle Atlas.',
    welcome_close: 'Close',

    music_on: 'Vivaldi — The Four Seasons',
    music_off: '♪ Spring — Allegro',
    music_aria_on: 'Turn on music',
    music_aria_off: 'Turn off music',

    scroll_top: 'Back to top',
  },

  es: {
    nav_home: 'Inicio',
    nav_about: 'Nosotros',
    nav_rooms: 'Alojamiento',
    nav_dining: 'Restaurante',
    nav_activities: 'Actividades',
    nav_gallery: 'Galería',
    nav_contact: 'Contacto',

    hero_location: 'Atlas Medio · Azrou · Marruecos',
    hero_title_1: "Ferme d'hôtes",
    hero_title_2: 'La Vallée',
    hero_desc: 'Tu remanso de paz en el corazón del Atlas Medio. Una experiencia auténtica y cálida, lejos del bullicio de la ciudad.',
    hero_cta: 'Descubre nuestras habitaciones',
    hero_slide: 'Diapositiva',

    about_label: 'Nuestra Historia',
    about_title_1: 'La hospitalidad',
    about_title_2: 'en el corazón de todo',
    about_p1: 'En Marruecos, la hospitalidad – <i>tinnubga</i> en amazigh, <i>diafa</i> en árabe – es mucho más que una costumbre: es un valor sagrado, un deber social y una alegría compartida. Acoger a un huésped es abrirle la casa y el corazón.',
    about_p2: 'En la <b>Granja de huéspedes La Vallée</b>, perpetuamos este valor ancestral en la experiencia turística moderna. Cada visitante es recibido como un miembro de la familia: acogida cálida, cocina local auténtica, descubrimiento cultural y natural del Atlas Medio.',
    about_p3: 'Nuestra granja no es solo un lugar de estancia, es un espacio donde la hospitalidad marroquí cobra vida. La calma del campo, la belleza de los paisajes y la calidez humana hacen de Marruecos una tierra de acogida única.',

    rooms_label: 'Alojamiento',
    rooms_title: 'Nuestras Habitaciones',
    rooms_desc: 'Todas nuestras habitaciones están equipadas con armario y baño privado con artículos de aseo gratuitos.',
    rooms_book: 'Reservar',
    room_double: 'Habitación Doble',
    room_double_desc: 'Ideal para parejas, con una cama doble cómoda y una cuidada decoración rústica.',
    room_quadruple: 'Habitación Cuádruple',
    room_quadruple_desc: 'Perfecta para familias o grupos de amigos, amplia y acogedora.',
    room_triple: 'Habitación Triple',
    room_triple_desc: 'Ideal para familias o amigos, un espacio generoso que garantiza comodidad y practicidad.',
    room_f_double_bed: 'Cama doble',
    room_f_private_bath: 'Baño privado',
    room_f_garden_view: 'Vista al jardín',
    room_f_quad_beds: '2 camas individuales + 1 cama doble grande',
    room_f_mountain_view: 'Vista a la montaña',
    room_f_triple_beds: '3 camas individuales',
    room_f_field_view: 'Vista a los campos',

    dining_gastro: 'Gastronomía',
    dining_title_1: 'Sabores',
    dining_title_2: 'del Terruño',
    dining_text: 'Aquí, cada plato cuenta una historia. Nuestras recetas marroquíes se preparan bajo pedido, con ingredientes frescos del terruño cuidadosamente seleccionados. Privilegiamos lo casero y auténtico: nada es precocido ni preparado de antemano. Para garantizarle una comida excepcional, le recomendamos reservar sus platos con antelación para que nuestro chef pueda sublimar los mejores productos del Atlas Medio.',
    dining_menu: 'Ver Menú',
    dining_local: 'Productos',
    dining_local2: 'locales',

    menu_tradition: 'Tradición y terruño del Atlas Medio',
    menu_title: 'Menú',
    menu_carte: 'a la carta',
    menu_year: '— 2026 —',
    menu_farmname: 'Granja de huéspedes',
    menu_farmname2: 'La Vallée',
    menu_open: 'Abrir menú',
    menu_header: 'Menú a la Carta',
    menu_header_sub: 'Granja de huéspedes La Vallée',
    menu_cover: 'Portada',
    menu_prev: 'Anterior',
    menu_next: 'Siguiente',
    menu_close: 'Cerrar',
    menu_bonappetit: '¡Buen Provecho!',
    menu_sec_breakfast: 'Desayuno',
    menu_sec_starters: 'Entrantes',
    menu_sec_moroccan: 'Platos Marroquíes',
    menu_sec_international: 'Cocina Internacional',
    menu_sec_kids: 'Platos para Niños',
    menu_sec_sides: 'Acompañamientos',
    menu_sec_desserts: 'Postres',
    menu_sec_drinks: 'Bebidas',

    act_label: 'Ocio',
    act_title: '¿Qué hacer en la Granja?',
    act_pool: 'Relax en la Piscina',
    act_pool_desc: 'Disfrute de nuestra piscina exterior para refrescarse admirando el paisaje.',
    act_garden: 'Paseos por el Jardín',
    act_garden_desc: 'Pasee por nuestro exuberante jardín y descubra nuestra huerta orgánica.',
    act_animals: 'Animales de la Granja',
    act_animals_desc: 'Una experiencia única para grandes y pequeños en contacto con nuestros animales.',
    act_games: 'Juegos y Ocio',
    act_games_desc: 'Billar, ping-pong o dardos para momentos de convivencia.',
    act_hiking: 'Excursiones Guiadas',
    act_hiking_desc: 'Explore el magnífico bosque de cedros con nuestros guías locales expertos.',
    act_lounge: 'Relax en el Salón',
    act_lounge_desc: 'Disfrute de nuestro salón con chimenea para leer o degustar un té de menta.',

    testi_label: 'Testimonios',
    testi_title: 'Lo que dicen nuestros visitantes',

    gallery_label: 'Fotos',
    gallery_title: 'Nuestra Galería',
    gallery_desc: 'Descubra en imágenes la belleza de nuestra granja, sus paisajes y su atmósfera única.',
    gallery_all: 'Todo',
    gallery_landscape: 'Paisaje',
    gallery_garden: 'Jardín',
    gallery_rooms: 'Habitaciones',
    gallery_interior: 'Interior',
    gallery_farm: 'La Granja',
    gallery_animals: 'Animales',
    gallery_dining: 'Restaurante',
    gallery_activities: 'Actividades',

    map_label: 'Ubicación',
    map_title: 'Encuéntranos',
    map_desc: 'Situada en el corazón del Atlas Medio, nuestra granja le acoge en Azrou, en un marco natural excepcional.',
    map_name: 'Granja de huéspedes La Vallée',
    map_address: 'Takmout aoud elma, ruta Meknès — Azrou, Marruecos',
    map_open: 'Abrir en Google Maps',

    contact_label: 'Contacto',
    contact_title: 'Contáctenos',
    contact_desc: 'Reserve su estancia ahora o háganos sus preguntas. Estamos deseando recibirle.',
    contact_address_title: 'Nuestra dirección',
    contact_address: 'Takmout aoud elma, ruta Meknès AZROU\ndouar boudraa, 53123 Azrou, Marruecos',
    contact_phone: 'Teléfono',
    contact_reservation: 'Reserva',
    contact_email: 'Correo electrónico',

    footer_tagline: 'Tu remanso de paz en el corazón del Atlas Medio.',
    footer_rights: '© 2024 Granja de huéspedes La Vallée. Todos los derechos reservados.',
    footer_powered: 'Powered by',

    wa_label: 'Reserva y Disponibilidad',
    wa_aria: 'Reservar por WhatsApp',
    wa_room_msg: 'Hola, me gustaría reservar la',

    welcome_title: '¡Bienvenido! 🌿',
    welcome_text: 'Descubra nuestro remanso de paz en el Atlas Medio marroquí.',
    welcome_close: 'Cerrar',

    music_on: 'Vivaldi — Las Cuatro Estaciones',
    music_off: '♪ Primavera — Allegro',
    music_aria_on: 'Activar música',
    music_aria_off: 'Desactivar música',

    scroll_top: 'Volver arriba',
  },

  it: {
    nav_home: 'Home',
    nav_about: 'Chi Siamo',
    nav_rooms: 'Alloggio',
    nav_dining: 'Ristorazione',
    nav_activities: 'Attività',
    nav_gallery: 'Galleria',
    nav_contact: 'Contatto',

    hero_location: 'Medio Atlante · Azrou · Marocco',
    hero_title_1: "Ferme d'hôtes",
    hero_title_2: 'La Vallée',
    hero_desc: "Il vostro rifugio di pace nel cuore del Medio Atlante. Un'esperienza autentica e calorosa, lontano dal trambusto della città.",
    hero_cta: 'Scopri le nostre camere',
    hero_slide: 'Diapositiva',

    about_label: 'La Nostra Storia',
    about_title_1: "L'ospitalità",
    about_title_2: 'al centro di tutto',
    about_p1: "In Marocco, l'ospitalità – <i>tinnubga</i> in amazigh, <i>diafa</i> in arabo – è molto più di un'usanza: è un valore sacro, un dovere sociale e una gioia condivisa. Accogliere un ospite significa aprirgli la casa e il cuore.",
    about_p2: "All'<b>Agriturismo La Vallée</b>, perpetuiamo questo valore ancestrale nell'esperienza turistica moderna. Ogni visitatore è accolto come un membro della famiglia: accoglienza calorosa, cucina locale autentica, scoperta culturale e naturale del Medio Atlante.",
    about_p3: "La nostra fattoria non è solo un luogo di soggiorno, è uno spazio dove l'ospitalità marocchina prende vita. La calma della campagna, la bellezza dei paesaggi e il calore umano che fanno del Marocco una terra di accoglienza unica.",

    rooms_label: 'Alloggio',
    rooms_title: 'Le Nostre Camere',
    rooms_desc: 'Tutte le nostre camere sono dotate di armadio e bagno privato con articoli da toilette gratuiti.',
    rooms_book: 'Prenota',
    room_double: 'Camera Doppia',
    room_double_desc: 'Ideale per le coppie, con letto matrimoniale confortevole e raffinato arredamento rustico.',
    room_quadruple: 'Camera Quadrupla',
    room_quadruple_desc: 'Perfetta per famiglie o gruppi di amici, spaziosa e accogliente.',
    room_triple: 'Camera Tripla',
    room_triple_desc: 'Ideale per famiglie o amici, uno spazio generoso che garantisce comfort e praticità.',
    room_f_double_bed: 'Letto matrimoniale',
    room_f_private_bath: 'Bagno privato',
    room_f_garden_view: 'Vista sul giardino',
    room_f_quad_beds: '2 letti singoli + 1 letto matrimoniale grande',
    room_f_mountain_view: 'Vista sulla montagna',
    room_f_triple_beds: '3 letti singoli',
    room_f_field_view: 'Vista sui campi',

    dining_gastro: 'Gastronomia',
    dining_title_1: 'Sapori',
    dining_title_2: 'del Territorio',
    dining_text: "Da noi, ogni piatto racconta una storia. Le nostre ricette marocchine sono preparate su ordinazione, con ingredienti freschi del territorio selezionati con cura. Privilegiamo il fatto in casa e l'autenticità: niente è precotto né preparato in anticipo. Per garantirvi un pasto eccezionale, vi consigliamo di prenotare i vostri piatti in anticipo affinché il nostro chef possa esaltare i migliori prodotti del Medio Atlante.",
    dining_menu: 'Vedi Menù',
    dining_local: 'Prodotti',
    dining_local2: 'locali',

    menu_tradition: 'Tradizione e territorio del Medio Atlante',
    menu_title: 'Menù',
    menu_carte: 'alla carta',
    menu_year: '— 2026 —',
    menu_farmname: 'Agriturismo',
    menu_farmname2: 'La Vallée',
    menu_open: 'Apri il menù',
    menu_header: 'Menù alla Carta',
    menu_header_sub: 'Agriturismo La Vallée',
    menu_cover: 'Copertina',
    menu_prev: 'Precedente',
    menu_next: 'Successivo',
    menu_close: 'Chiudi',
    menu_bonappetit: 'Buon Appetito',
    menu_sec_breakfast: 'Colazione',
    menu_sec_starters: 'Antipasti',
    menu_sec_moroccan: 'Piatti Marocchini',
    menu_sec_international: 'Cucina Internazionale',
    menu_sec_kids: 'Piatti per Bambini',
    menu_sec_sides: 'Contorni',
    menu_sec_desserts: 'Dolci',
    menu_sec_drinks: 'Bevande',

    act_label: 'Svago',
    act_title: 'Cosa Fare alla Fattoria?',
    act_pool: 'Relax in Piscina',
    act_pool_desc: 'Godetevi la nostra piscina esterna per rinfrescarvi ammirando il paesaggio.',
    act_garden: 'Passeggiate in Giardino',
    act_garden_desc: 'Passeggiate nel nostro rigoglioso giardino e scoprite il nostro orto biologico.',
    act_animals: 'Animali della Fattoria',
    act_animals_desc: "Un'esperienza unica per grandi e piccini a contatto con i nostri animali.",
    act_games: 'Giochi e Svago',
    act_games_desc: 'Biliardo, ping-pong o freccette per momenti di convivialità.',
    act_hiking: 'Escursioni Guidate',
    act_hiking_desc: 'Esplorate la magnifica foresta di cedri con le nostre guide locali esperte.',
    act_lounge: 'Relax nel Salotto',
    act_lounge_desc: 'Godetevi il nostro salotto con camino per leggere o gustare un tè alla menta.',

    testi_label: 'Testimonianze',
    testi_title: 'Cosa dicono i nostri visitatori',

    gallery_label: 'Foto',
    gallery_title: 'La Nostra Galleria',
    gallery_desc: "Scoprite in immagini la bellezza del nostro agriturismo, i suoi paesaggi e la sua atmosfera unica.",
    gallery_all: 'Tutto',
    gallery_landscape: 'Paesaggio',
    gallery_garden: 'Giardino',
    gallery_rooms: 'Camere',
    gallery_interior: 'Interni',
    gallery_farm: 'La Fattoria',
    gallery_animals: 'Animali',
    gallery_dining: 'Ristorazione',
    gallery_activities: 'Attività',

    map_label: 'Posizione',
    map_title: 'Trovateci',
    map_desc: "Situato nel cuore del Medio Atlante, il nostro agriturismo vi accoglie ad Azrou, in un contesto naturale eccezionale.",
    map_name: 'Agriturismo La Vallée',
    map_address: 'Takmout aoud elma, strada Meknès — Azrou, Marocco',
    map_open: 'Apri in Google Maps',

    contact_label: 'Contatto',
    contact_title: 'Contattateci',
    contact_desc: 'Prenotate il vostro soggiorno ora o fateci le vostre domande. Non vediamo l\'ora di accogliervi.',
    contact_address_title: 'Il nostro indirizzo',
    contact_address: 'Takmout aoud elma, strada Meknès AZROU\ndouar boudraa, 53123 Azrou, Marocco',
    contact_phone: 'Telefono',
    contact_reservation: 'Prenotazione',
    contact_email: 'Email',

    footer_tagline: 'Il vostro rifugio di pace nel cuore del Medio Atlante.',
    footer_rights: '© 2024 Agriturismo La Vallée. Tutti i diritti riservati.',
    footer_powered: 'Powered by',

    wa_label: 'Prenotazione e Disponibilità',
    wa_aria: 'Prenota tramite WhatsApp',
    wa_room_msg: 'Salve, vorrei prenotare la',

    welcome_title: 'Benvenuti! 🌿',
    welcome_text: "Scoprite il nostro rifugio di pace nel cuore del Medio Atlante marocchino.",
    welcome_close: 'Chiudi',

    music_on: 'Vivaldi — Le Quattro Stagioni',
    music_off: '♪ Primavera — Allegro',
    music_aria_on: 'Attiva musica',
    music_aria_off: 'Disattiva musica',

    scroll_top: 'Torna su',
  },
};

export default t;
